import { useState, useRef, useEffect } from 'react';
import { kmFormatSpec, kmSetTarget, kmSetComplete, TOURNAMENT_TYPES } from '../../engine';
import { kmAccentForGender, kmGroupNumber } from './kmLabels';
import { KMClassTag } from './KMClassTag';
import { NumberPadPopup } from '../americano/NumberPadPopup';
import { deviceFillBoxStyle } from '../banepad/deviceFill';

// deviation: STARPOINT_LOGO_WHITE_URI was defined in the original artifact
// (legacy/App.jsx); copied verbatim as a module-local const (public asset path,
// not logic) so this component is self-contained. Mirrors the precedent in
// src/components/banepad/CourtPadCard.tsx.
const STARPOINT_LOGO_WHITE_URI = '/starpoint-logo-white.png';

// deviation: readableTextOn was a pure helper defined at module scope in the
// original artifact (legacy/App.jsx); copied verbatim as a module-local helper
// (no closure state) so this component is self-contained. Behaviour unchanged.
function readableTextOn(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return '#ffffff';
  const r = parseInt(h.slice(0, 2), 16),
    g = parseInt(h.slice(2, 4), 16),
    b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0a0b10' : '#ffffff';
}

export interface KMCourtCardProps {
  [key: string]: any;
}

// Extracted verbatim from the original artifact (legacy/App.jsx). Dark Padel24-style court
// panel for the KM banepad. Note: the `TeamBlock`/`TeamBlockLight` used below are
// closure-local components defined inside this function, NOT the module-level
// TeamBlock stub — so it is not imported here.
export function KMCourtCard({
  court,
  matches = [],
  pairs = [],
  allPlayers = [],
  divisions = [],
  nowMin,
  today,
  onSaveScore,
  onClickScore,
  onPlayerClick,
  onScoreChange = null,
  clock,
  locked = false,
  formatType = 'klubbmesterskap',
  footer = null,
  fill = false,
}: KMCourtCardProps) {
  // deviation: type-only (the original artifact was @ts-nocheck) — set objects carry
  // an optional `tb` (tiebreak) field the literal inference drops; annotate any[].
  const [sets, setSets] = useState<any[]>([{ t1: 0, t2: 0 }]);
  const [padSide, setPadSide] = useState(null); // 1 | 2 | null — which number the quick-picker is open for
  const padAnchorRef = useRef(null);
  const [history, setHistory] = useState([]); // undo stack (previous set states)
  const [confirmReset, setConfirmReset] = useState(false);
  const [woOpen, setWoOpen] = useState(false);
  const activeRef = useRef(null);
  const scrollRef = useRef(null);

  const minsOf = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const sorted = [...matches].sort(
    (a, b) =>
      (a.date || '').localeCompare(b.date || '') ||
      (a.startTime || '').localeCompare(b.startTime || ''),
  );
  const active =
    sorted.find(
      (m) =>
        m.date === today && minsOf(m.startTime) <= nowMin && minsOf(m.endTime) > nowMin && !m.score,
    ) || sorted.find((m) => !m.score && m.team1?.pairId && m.team2?.pairId);
  const completed = sorted.filter((m) => m.score);
  const upcoming = sorted.filter(
    (m) => !m.score && m !== active && m.team1?.pairId && m.team2?.pairId,
  );

  // Init counters from the active match. Resume from a saved in-progress score
  // (liveScore — persisted continuously without finalising) when present, else
  // from the finalised score, else empty. Additive: matches without liveScore
  // behave exactly as before.
  useEffect(() => {
    const s = active?.liveScore?.sets?.length ? active.liveScore.sets : active?.score?.sets;
    setSets(
      Array.isArray(s) && s.length
        ? s.map((x) => ({ t1: x.t1 || 0, t2: x.t2 || 0, ...(x.tb ? { tb: x.tb } : {}) }))
        : [{ t1: 0, t2: 0 }],
    );
    setHistory([]);
    setConfirmReset(false);
    setWoOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);
  // Keep the active match at a fixed position in the feed (~18% from the top). The
  // bottom spacer guarantees scroll room, so the window doesn't "sag" downward as matches finish.
  useEffect(() => {
    const c = scrollRef.current,
      el = activeRef.current;
    if (!c || !el) return undefined;
    const id = requestAnimationFrame(() => {
      const pos = el.offsetTop - c.offsetTop;
      c.scrollTop = Math.max(0, pos - c.clientHeight * 0.18);
    });
    return () => cancelAnimationFrame(id);
  }, [active?.id, completed.length]);

  // deviation: `accentFor` was dead in the original artifact too (never referenced
  // in the JSX below) — kept verbatim for byte-fidelity; `_`-prefixed rather than removed.
  const _accentFor = (m) => {
    const d = divisions.find((x) => x.id === m?.divisionId);
    return d ? kmAccentForGender(d.classGender) : '#c2f02f';
  };
  const getPair = (id) => pairs.find((p) => p.id === id);
  const eloOfPlayer = (pid, m) => {
    const pre = m?.playerEloPre?.[pid];
    if (pre != null) return Math.round(pre);
    const pl = allPlayers.find((p) => p.id === pid);
    return pl?.elo ?? null;
  };
  // Team average: use pre-match ELO if set, otherwise compute from the players' current ELO.
  const avgEloFor = (p, side, m) => {
    const pre = m?.teamEloPre?.[side];
    if (pre != null) return Math.round(pre);
    if (!p) return null;
    const e1 = eloOfPlayer(p.p1Id, m),
      e2 = eloOfPlayer(p.p2Id, m);
    if (e1 == null || e2 == null) return null;
    return Math.round((e1 + e2) / 2);
  };

  const lime = '#c2f02f';
  const muted = '#8a9a8f';

  // Team block (two names + optional ELO/change)
  // deviation: type-only (the original artifact was @ts-nocheck) — inferred props
  // made `align` required, but a call-site omits it; type the param as any.
  const TeamBlock = ({ team, align, m, change, note }: any) => {
    const p = getPair(team?.pairId);
    const names = p ? [p.p1Name, p.p2Name] : null;
    const ids = p ? [p.p1Id, p.p2Id] : [];
    return (
      <div className={`flex-1 min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
        {names ? (
          names.map((nm, i) => (
            <div key={i} className="truncate text-white font-semibold text-sm leading-tight">
              {onPlayerClick ? (
                <button
                  type="button"
                  onClick={() => onPlayerClick({ id: ids[i] })}
                  className="hover:underline"
                >
                  {nm}
                </button>
              ) : (
                nm
              )}
            </div>
          ))
        ) : (
          <div className="text-[12px] font-mono italic" style={{ color: muted }}>
            venter
          </div>
        )}
        {note && (
          <div className={`mt-0.5 ${align === 'right' ? 'text-right' : ''}`}>
            <span
              className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(220,38,38,0.22)', color: '#fca5a5' }}
            >
              {note}
            </span>
          </div>
        )}
        {p && (
          <div
            className={`flex items-center gap-1.5 mt-0.5 ${align === 'right' ? 'justify-end' : ''}`}
          >
            <span className="text-[10px] font-mono" style={{ color: muted }}>
              snitt {avgEloFor(p, align === 'right' ? 't2' : 't1', m) ?? '–'}
            </span>
            {change != null && (
              <span
                className="text-[10px] font-mono font-bold px-1 rounded"
                style={{ background: change >= 0 ? '#3f6212' : '#7f1d1d', color: '#fff' }}
              >
                {change >= 0 ? '+' : ''}
                {change}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  // Light variant (dark text) for the white in-progress match.
  const TeamBlockLight = ({ team, align, m }) => {
    const p = getPair(team?.pairId);
    const names = p ? [p.p1Name, p.p2Name] : null;
    const ids = p ? [p.p1Id, p.p2Id] : [];
    return (
      <div className={`flex-1 min-w-0 ${align === 'right' ? 'text-right' : ''}`}>
        {names ? (
          names.map((nm, i) => (
            <div
              key={i}
              className="truncate font-bold text-base leading-tight"
              style={{ color: '#0f1611' }}
            >
              {onPlayerClick ? (
                <button
                  type="button"
                  onClick={() => onPlayerClick({ id: ids[i] })}
                  className="hover:underline"
                >
                  {nm}
                </button>
              ) : (
                nm
              )}
            </div>
          ))
        ) : (
          <div className="text-[12px] font-mono italic" style={{ color: '#6b766f' }}>
            venter
          </div>
        )}
        {p && (
          <div className={`mt-0.5 ${align === 'right' ? 'text-right' : ''}`}>
            <span className="text-[10px] font-mono" style={{ color: '#6b766f' }}>
              snitt ELO {avgEloFor(p, align === 'right' ? 't2' : 't1', m) ?? '–'}
            </span>
          </div>
        )}
      </div>
    );
  };

  const matchCtx = (m) => {
    const rl =
      {
        r16: '8-delsfinale',
        qf: 'Kvartfinale',
        sf: 'Semifinale',
        f: 'Finale',
        bronze: 'Bronsefinale',
        b_r16: 'B 8-del',
        b_qf: 'B kvart',
        b_sf: 'B semi',
        b_f: 'B finale',
        b_bronze: 'B bronse',
      }[m.round] || 'Sluttspill';
    return `Kamp ${m.matchNumber || ''} · ${m.phase === 'knockout' ? rl : `Gruppe ${kmGroupNumber(m.groupName)}${m.roundIndex ? ` · R${m.roundIndex}` : ''}`}`;
  };
  // Class/division info for a match — used for the badge + label in the banepad.
  const matchDivInfo = (m) => {
    const d = divisions.find((x) => x.id === m?.divisionId);
    if (!d) return null;
    const clsName = d.className || '';
    return {
      divName: d.name,
      className: clsName,
      label: `${clsName} ${d.name}`.trim(),
      gender: d.classGender,
    };
  };

  // Game-by-game evaluation for the active match (respects match.matchFormat)
  const fmt = kmFormatSpec(active?.matchFormat);
  const fmtLabel = fmt.superTiebreak
    ? 'Best of 3 · super-tiebreak til 10 ved 1-1'
    : fmt.sets === 1
      ? fmt.shortSet
        ? 'Ett kort sett til 4'
        : 'Ett sett til 6'
      : 'Best of 3 · fullt 3. sett';
  const evalSets = (arr) => {
    const needed = Math.ceil(fmt.sets / 2);
    let w1 = 0,
      w2 = 0,
      complete = false;
    const done = [];
    for (let i = 0; i < arr.length; i++) {
      const isDecider = w1 === needed - 1 && w2 === needed - 1;
      const tgt = kmSetTarget(fmt, isDecider);
      const c = kmSetComplete(arr[i], tgt);
      done[i] = c;
      if (c) {
        if (arr[i].t1 > arr[i].t2) w1++;
        else if (arr[i].t2 > arr[i].t1) w2++;
        if (w1 === needed || w2 === needed) complete = true;
      }
      if (complete) break;
    }
    const isDeciderNow = w1 === needed - 1 && w2 === needed - 1;
    return { w1, w2, done, complete, curTarget: kmSetTarget(fmt, isDeciderNow) };
  };
  const ev = evalSets(sets);
  const curIdx = sets.length - 1;
  // Changes the set list and saves the previous state in the undo history.
  const commitSets = (next) => {
    setHistory((h) => [...h, sets]);
    setSets(next);
    setConfirmReset(false);
    // Persist the in-progress score continuously (live score) WITHOUT finalising
    // the match. The match stays "active" (no .score) until «Send resultat».
    if (onScoreChange && active && !locked) {
      onScoreChange({
        ...active,
        liveScore: { sets: next.filter((s) => s.t1 || s.t2 || s.tb), updatedAt: Date.now() },
      });
    }
  };
  // Persist the in-progress score after a non-commit change (undo/reset).
  const persistLive = (arr) => {
    if (onScoreChange && active && !locked) {
      onScoreChange({
        ...active,
        liveScore: { sets: arr.filter((s) => s.t1 || s.t2 || s.tb), updatedAt: Date.now() },
      });
    }
  };
  const undo = () =>
    setHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setSets(prev);
      persistLive(prev);
      return h.slice(0, -1);
    });
  const addGame = (team) => {
    if (ev.complete) return;
    const arr = sets.map((s) => ({ ...s }));
    const idx = arr.length - 1;
    const key = team === 1 ? 't1' : 't2';
    // At 6-6: play a tiebreak (points to 7, win by 2) → the set becomes 7-6.
    if (atRegularTb) {
      const tb = { ...(arr[idx].tb || { t1: 0, t2: 0 }) };
      tb[key]++;
      arr[idx].tb = tb;
      if (Math.max(tb.t1, tb.t2) >= 7 && Math.abs(tb.t1 - tb.t2) >= 2) {
        arr[idx].t1 = tb.t1 > tb.t2 ? 7 : 6;
        arr[idx].t2 = tb.t2 > tb.t1 ? 7 : 6;
        const ev2 = evalSets(arr);
        if (!ev2.complete) arr.push({ t1: 0, t2: 0 });
      }
      commitSets(arr);
      return;
    }
    arr[idx][key]++;
    const ev2 = evalSets(arr);
    if (!ev2.complete && ev2.done[idx]) arr.push({ t1: 0, t2: 0 });
    commitSets(arr);
  };
  const subGame = (team) => {
    const arr = sets.map((s) => ({ ...s }));
    let idx = arr.length - 1;
    const key = team === 1 ? 't1' : 't2';
    // In a tiebreak: subtract from the tiebreak points first.
    if (atRegularTb && arr[idx].tb && (arr[idx].tb.t1 || arr[idx].tb.t2)) {
      const tb = { ...arr[idx].tb };
      if (tb[key] > 0) tb[key]--;
      arr[idx].tb = tb;
      commitSets(arr);
      return;
    }
    if (arr[idx].t1 === 0 && arr[idx].t2 === 0 && arr.length > 1) {
      arr.pop();
      idx--;
    }
    if (arr[idx][key] > 0) arr[idx][key]--;
    commitSets(arr);
  };
  const canSend = ev.complete;
  const buildScore = () => ({ t1: ev.w1, t2: ev.w2, sets: sets.filter((s) => s.t1 || s.t2) });
  // Walkover: the team that didn't show up loses. The opponent wins 6-0(,6-0). Kept out of ELO.
  const sendWO = (winnerSide) => {
    if (locked || !active) return;
    const setsCount = fmt.sets === 1 ? 1 : 2;
    const woSets = Array.from({ length: setsCount }, () =>
      winnerSide === 't1' ? { t1: 6, t2: 0 } : { t1: 0, t2: 6 },
    );
    // DEVIATION (lint): the original artifact used the short-circuit expression
    // `onSaveScore && onSaveScore({...})`, which trips no-unused-expressions;
    // rewritten as an `if` with identical behaviour.
    if (onSaveScore)
      onSaveScore({
        ...active,
        score: {
          t1: winnerSide === 't1' ? setsCount : 0,
          t2: winnerSide === 't2' ? setsCount : 0,
          sets: woSets,
          walkover: true,
          woWinner: winnerSide,
          woAt: Date.now(),
        },
        completedAt: Date.now(),
      });
  };
  // Has a result been started? (If so, we hide the WO button.)
  const hasEnteredScore =
    ev.w1 > 0 || ev.w2 > 0 || sets.some((s) => (s.t1 || 0) > 0 || (s.t2 || 0) > 0 || s.tb);
  // Before match start? (Then it's strictly speaking not a walkover yet.)
  const beforeStart =
    active && (active.date > today || (active.date === today && minsOf(active.startTime) > nowMin));
  // Overdue = past today's match start, no points entered yet.
  const overdue =
    active &&
    active.date === today &&
    minsOf(active.startTime) <= nowMin &&
    !ev.w1 &&
    !ev.w2 &&
    sets.length === 1 &&
    sets[0].t1 === 0 &&
    sets[0].t2 === 0;

  // Tiebreak status (labels only — +/− and the quick-pick handle the points themselves)
  const tbBase = fmt.shortSet ? 4 : 6;
  const isSuperTb = !ev.complete && ev.curTarget.isTb; // deciding super-tiebreak
  const atRegularTb =
    !ev.complete && !ev.curTarget.isTb && sets[curIdx].t1 === tbBase && sets[curIdx].t2 === tbBase; // 6-6

  // Smart suggestion: given that one team has n games/points, what's likely for the opponent?
  const smartOpp = (n, tgt) => {
    if (tgt.isTb) return n < tgt.target - 1 ? tgt.target : n + 2; // super-tb to 10
    const T = tgt.target,
      C = tgt.cap;
    if (n < T - 1) return T; // 0–4 → 6
    if (n === T - 1) return C; // 5 → 7
    if (n === T) return C; // 6 → 7 (tiebreak)
    return Math.max(0, n - 2); // 7+ → n-2
  };
  // Smart tiebreak suggestion (to 7, win by 2). n is assumed to be the loser's points.
  const smartTb = (n) => (n < 6 ? 7 : n >= 7 ? Math.max(0, n - 2) : 8);
  // Quick-pick: set games/points for one team + a smart suggestion. Adjustable, NOT submitted.
  const quickSet = (team, n) => {
    const arr = sets.map((s) => ({ ...s }));
    const idx = arr.length - 1;
    if (atRegularTb) {
      const opp = smartTb(n);
      const tb = team === 1 ? { t1: n, t2: opp } : { t1: opp, t2: n };
      arr[idx].tb = tb;
      if (Math.max(tb.t1, tb.t2) >= 7 && Math.abs(tb.t1 - tb.t2) >= 2) {
        arr[idx].t1 = tb.t1 > tb.t2 ? 7 : 6;
        arr[idx].t2 = tb.t2 > tb.t1 ? 7 : 6;
        const ev2 = evalSets(arr);
        if (!ev2.complete) arr.push({ t1: 0, t2: 0 });
      }
      commitSets(arr);
      return;
    }
    const opp = smartOpp(n, ev.curTarget);
    if (team === 1) {
      arr[idx].t1 = n;
      arr[idx].t2 = opp;
    } else {
      arr[idx].t2 = n;
      arr[idx].t1 = opp;
    }
    const ev2 = evalSets(arr);
    if (!ev2.complete && ev2.done[idx]) arr.push({ t1: 0, t2: 0 });
    commitSets(arr);
  };
  // Max for the quick-picker (NumberPadPopup): super-tiebreak 16, regular tiebreak 9, otherwise 7.
  const padMax = isSuperTb ? 16 : atRegularTb ? 9 : fmt.shortSet ? 5 : 7;

  return (
    <div
      className="rounded-2xl overflow-hidden relative flex flex-col"
      style={{
        background: 'linear-gradient(135deg, #1d2823 0%, #131c18 50%, #0f1611 100%)',
        boxShadow:
          '0 12px 40px -12px rgba(15, 22, 17, 0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
        border: '7px solid #232f29',
        aspectRatio: '4 / 3',
        width: '100%',
        maxWidth: 900,
        maxHeight: '85vh',
        // Device kiosk: fill the whole tablet (same as the Americano result pad).
        ...deviceFillBoxStyle(fill),
      }}
    >
      {/* Header — BANE N · Padel24 · clock */}
      <div
        className="px-5 py-3 flex items-center justify-between flex-shrink-0"
        style={{ background: 'rgba(0,0,0,0.32)', borderBottom: '1px solid rgba(194,240,47,0.12)' }}
      >
        <div className="flex items-baseline gap-2">
          <span
            className="text-[11px] font-mono uppercase tracking-[0.2em]"
            style={{ color: muted }}
          >
            Bane
          </span>
          <span className="text-2xl font-bold" style={{ color: lime }}>
            {court}
          </span>
        </div>
        <div className="flex items-center justify-center">
          <img
            src={STARPOINT_LOGO_WHITE_URI}
            alt="Starpoint"
            style={{ height: 24, width: 'auto', objectFit: 'contain' }}
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: muted }}>
            {matches.length} kamper
          </span>
          {clock && (
            <span className="text-lg font-mono font-bold text-white tabular-nums">{clock}</span>
          )}
        </div>
      </div>

      {/* Tournament-type stripe — between the Starpoint logo and the matches */}
      {(() => {
        const tt = TOURNAMENT_TYPES[formatType] || TOURNAMENT_TYPES.klubbmesterskap;
        return (
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ background: tt.color, padding: '6px 16px' }}
          >
            <span
              className="font-mono font-bold uppercase"
              style={{ fontSize: 12, letterSpacing: '0.28em', color: readableTextOn(tt.color) }}
            >
              {tt.label}
            </span>
          </div>
        );
      })()}

      {/* Content — scrolls inside the iPad frame */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin">
        {/* Most recent finished match */}
        {completed.map((m) => {
          const t1Won = (m.score.t1 ?? 0) > (m.score.t2 ?? 0);
          const isWO = m.score.walkover;
          return (
            <div
              key={m.id}
              className="px-4 py-2.5 flex items-center gap-2"
              style={{
                borderBottom: '1px solid rgba(125,155,185,0.10)',
                background: 'linear-gradient(180deg, rgba(71,100,130,0.28), rgba(71,100,130,0.12))',
                opacity: 0.85,
              }}
            >
              {t1Won && (
                <span className="flex-shrink-0" style={{ color: lime }}>
                  ✓
                </span>
              )}
              <TeamBlock
                team={m.team1}
                m={m}
                change={m.teamEloChange?.t1}
                note={isWO && !t1Won ? 'møtte ikke' : null}
              />
              {isWO ? (
                <div className="flex flex-col items-center font-mono leading-tight flex-shrink-0">
                  <span
                    className="text-sm font-bold px-2 py-0.5 rounded"
                    style={{ background: 'rgba(220,38,38,0.18)', color: '#fca5a5' }}
                  >
                    WO
                  </span>
                  {m.score.woAt && (
                    <span className="text-[9px] mt-0.5" style={{ color: muted }}>
                      reg.{' '}
                      {new Date(m.score.woAt).toLocaleTimeString('no-NO', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 font-mono font-bold flex-shrink-0">
                  <span className="text-lg" style={{ color: t1Won ? lime : muted }}>
                    {m.score.t1}
                  </span>
                  <span style={{ color: muted }}>–</span>
                  <span className="text-lg" style={{ color: !t1Won ? lime : muted }}>
                    {m.score.t2}
                  </span>
                </div>
              )}
              <TeamBlock
                team={m.team2}
                align="right"
                m={m}
                change={m.teamEloChange?.t2}
                note={isWO && t1Won ? 'møtte ikke' : null}
              />
              {!t1Won && (
                <span className="flex-shrink-0" style={{ color: lime }}>
                  ✓
                </span>
              )}
              {onClickScore && (
                <button
                  type="button"
                  onClick={() => onClickScore(m)}
                  className="text-[10px] font-mono uppercase px-2 py-1 rounded flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.08)', color: muted }}
                >
                  Endre
                </button>
              )}
            </div>
          );
        })}

        {/* In-progress match — LIGHT/WHITE (same as Padel24) */}
        {active ? (
          <div
            ref={activeRef}
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f7f9f1 100%)',
              color: '#0f1611',
              borderTop: '3px solid #c2f02f',
              borderBottom: '3px solid #c2f02f',
            }}
          >
            <div className="p-4">
              {/* Status row */}
              <div className="flex items-center gap-2 mb-3">
                <span className="relative flex h-3 w-3">
                  <span
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ background: '#84cc16' }}
                  />
                  <span
                    className="relative inline-flex rounded-full h-3 w-3"
                    style={{ background: '#84cc16' }}
                  />
                </span>
                <span
                  className="font-mono text-xs uppercase tracking-wider font-bold"
                  style={{ color: '#3f6212' }}
                >
                  PÅGÅR
                </span>
                {(() => {
                  const di = matchDivInfo(active);
                  return di ? <KMClassTag gender={di.gender} divName={di.divName} /> : null;
                })()}
                <span
                  className="font-mono text-xs uppercase tracking-wider"
                  style={{ color: '#3a4a40' }}
                >
                  {matchCtx(active)}
                </span>
                {active.startTime && (
                  <span className="font-mono text-xs" style={{ color: '#6b766f' }}>
                    · {active.startTime}
                  </span>
                )}
              </div>
              {/* Player rows */}
              {/* Players on the sides; set standings, history, and score CENTERED */}
              {(() => {
                const renderTeamCol = (team, ti) => (
                  <div className={ti === 1 ? 'text-right' : ''}>
                    <TeamBlockLight
                      team={team === 1 ? active.team1 : active.team2}
                      align={ti === 1 ? 'right' : undefined}
                      m={active}
                    />
                  </div>
                );
                const completedSets = sets.filter((s, i) => ev.done[i] && (s.t1 || s.t2 || s.tb));
                return (
                  <div
                    className="grid items-center gap-3"
                    style={{ gridTemplateColumns: '1fr auto 1fr' }}
                  >
                    {renderTeamCol(1, 0)}
                    {/* Center: vs + stacked history + (w1) score (w2) */}
                    <div className="flex flex-col items-center justify-start pt-1 px-1">
                      <span
                        className="font-display text-xs mb-1"
                        style={{ color: '#0f1611', opacity: 0.3 }}
                      >
                        vs
                      </span>
                      {/* Completed sets — stacked above one another */}
                      {completedSets.length > 0 && (
                        <div className="flex flex-col items-center gap-0.5 mb-1.5">
                          {completedSets.map((s, i) => {
                            const t1Won = s.t1 > s.t2;
                            return (
                              <div
                                key={i}
                                className="font-mono font-bold leading-none flex items-center gap-1"
                                style={{ fontSize: 16 }}
                              >
                                <span style={{ color: t1Won ? '#2d5a08' : '#9aa69d' }}>{s.t1}</span>
                                <span style={{ color: '#0f1611', opacity: 0.25 }}>–</span>
                                <span style={{ color: !t1Won ? '#2d5a08' : '#9aa69d' }}>
                                  {s.t2}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {atRegularTb && (
                        <span
                          className="font-mono text-[9px] uppercase tracking-wider mb-0.5 px-1.5 py-0.5 rounded-full"
                          style={{ background: 'rgba(194,240,47,0.30)', color: '#3f6212' }}
                        >
                          tiebreak · 6-6
                        </span>
                      )}
                      {!ev.complete &&
                        (() => {
                          const d1 = atRegularTb ? sets[curIdx].tb?.t1 || 0 : sets[curIdx].t1;
                          const d2 = atRegularTb ? sets[curIdx].tb?.t2 || 0 : sets[curIdx].t2;
                          const showW = ev.w1 + ev.w2 > 0;
                          const winBadge = (w, lead) => (
                            <span
                              className="font-mono font-bold leading-none"
                              style={{ fontSize: 16, color: lead ? '#2d5a08' : '#9aa69d' }}
                            >
                              ({w})
                            </span>
                          );
                          return (
                            <div
                              className="flex items-center gap-2 font-mono font-bold leading-none"
                              style={{ color: '#0f1611' }}
                            >
                              {showW && winBadge(ev.w1, ev.w1 > ev.w2)}
                              <button
                                disabled={locked}
                                onClick={(e) => {
                                  padAnchorRef.current = e.currentTarget;
                                  setPadSide(1);
                                }}
                                className="rounded-lg active:scale-95"
                                title={locked ? '' : 'Trykk for hurtigvalg'}
                                style={{
                                  fontSize: 44,
                                  padding: '0 4px',
                                  background:
                                    padSide === 1 ? 'rgba(194,240,47,0.30)' : 'transparent',
                                  cursor: locked ? 'default' : 'pointer',
                                }}
                              >
                                {d1}
                              </button>
                              <span style={{ fontSize: 28, opacity: 0.25 }}>–</span>
                              <button
                                disabled={locked}
                                onClick={(e) => {
                                  padAnchorRef.current = e.currentTarget;
                                  setPadSide(2);
                                }}
                                className="rounded-lg active:scale-95"
                                title={locked ? '' : 'Trykk for hurtigvalg'}
                                style={{
                                  fontSize: 44,
                                  padding: '0 4px',
                                  background:
                                    padSide === 2 ? 'rgba(194,240,47,0.30)' : 'transparent',
                                  cursor: locked ? 'default' : 'pointer',
                                }}
                              >
                                {d2}
                              </button>
                              {showW && winBadge(ev.w2, ev.w2 > ev.w1)}
                            </div>
                          );
                        })()}
                    </div>
                    {renderTeamCol(2, 1)}
                  </div>
                );
              })()}

              {/* +/− grouped in the center: team 1 [− +]  ·  team 2 [+ −] */}
              {!ev.complete && !locked && (
                <div className="flex items-center justify-center gap-6 mt-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => subGame(1)}
                      className="font-mono text-sm rounded"
                      style={{
                        width: 34,
                        height: 34,
                        color: '#6b766f',
                        border: '1px solid rgba(15,22,17,0.12)',
                      }}
                    >
                      −
                    </button>
                    <button
                      onClick={() => addGame(1)}
                      className="flex items-center justify-center font-mono font-bold active:scale-95"
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 14,
                        background: '#c2f02f',
                        color: '#0f1611',
                        fontSize: 28,
                        boxShadow: '0 2px 8px -2px rgba(124,168,31,0.3)',
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => addGame(2)}
                      className="flex items-center justify-center font-mono font-bold active:scale-95"
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 14,
                        background: '#c2f02f',
                        color: '#0f1611',
                        fontSize: 28,
                        boxShadow: '0 2px 8px -2px rgba(124,168,31,0.3)',
                      }}
                    >
                      +
                    </button>
                    <button
                      onClick={() => subGame(2)}
                      className="font-mono text-sm rounded"
                      style={{
                        width: 34,
                        height: 34,
                        color: '#6b766f',
                        border: '1px solid rgba(15,22,17,0.12)',
                      }}
                    >
                      −
                    </button>
                  </div>
                </div>
              )}

              {/* Status for the current set / finished */}
              <div
                className="text-center font-mono text-[10px] uppercase tracking-wider mt-2"
                style={{ color: ev.complete ? '#3f6212' : '#6b766f' }}
              >
                {ev.complete
                  ? `Ferdig — ${ev.w1}–${ev.w2} i sett`
                  : isSuperTb
                    ? `Super-tiebreak til ${ev.curTarget.target}`
                    : atRegularTb
                      ? 'Tiebreak til 7 (vinn med 2) — settet blir 7–6'
                      : `Sett ${curIdx + 1} — til ${ev.curTarget.target}`}{' '}
                · {fmtLabel}
              </div>

              {/* Quick-picker — small number list anchored to the number you clicked (like Americano). */}
              {padSide !== null && !ev.complete && !locked && (
                <NumberPadPopup
                  anchorRef={padAnchorRef}
                  maxValue={padMax}
                  onPick={(n) => {
                    quickSet(padSide, n);
                    setPadSide(null);
                  }}
                  onClose={() => setPadSide(null)}
                />
              )}

              {locked ? (
                <div
                  className="mt-3 pt-3 text-center font-mono text-[11px] uppercase tracking-wider"
                  style={{ borderTop: '1px solid rgba(15,22,17,0.08)', color: '#6b766f' }}
                >
                  🔒 Start turneringen for å registrere resultater
                </div>
              ) : (
                <>
                  {/* Walkover (WO) — available until you start entering a result */}
                  {!woOpen && !hasEnteredScore && (
                    <button
                      onClick={() => setWoOpen(true)}
                      className="w-full mt-2 py-1.5 rounded-lg font-mono text-[11px] uppercase tracking-wider"
                      style={
                        overdue
                          ? {
                              background: 'rgba(220,38,38,0.08)',
                              color: '#b91c1c',
                              border: '1px solid rgba(220,38,38,0.25)',
                            }
                          : {
                              background: 'transparent',
                              color: '#6b766f',
                              border: '1px solid rgba(15,22,17,0.12)',
                            }
                      }
                    >
                      {overdue ? '⚠ Motstander ikke møtt? Registrer WO' : 'Registrer walkover (WO)'}
                    </button>
                  )}
                  {woOpen && (
                    <div
                      className="mt-2 p-2.5 rounded-xl"
                      style={{ background: '#0f161108', border: '1px solid rgba(15,22,17,0.12)' }}
                    >
                      {beforeStart && (
                        <div
                          className="mb-1.5 px-2 py-1.5 rounded-lg text-[10px] font-mono leading-snug"
                          style={{ background: 'rgba(180,120,0,0.12)', color: '#92600a' }}
                        >
                          ⏳ Kampen har ikke startet ennå (start {active.startTime}). Walkover
                          gjelder normalt først hvis et lag ikke møter til kampstart.
                        </div>
                      )}
                      <div
                        className="font-mono text-[10px] uppercase tracking-wider mb-1.5 text-center"
                        style={{ color: '#b91c1c' }}
                      >
                        Walkover — hvilket lag møtte ikke?
                      </div>
                      <div className="flex gap-2">
                        {[1, 2].map((team) => {
                          const p = getPair(
                            team === 1 ? active.team1?.pairId : active.team2?.pairId,
                          );
                          return (
                            <button
                              key={team}
                              onClick={() => {
                                sendWO(team === 1 ? 't2' : 't1');
                                setWoOpen(false);
                              }}
                              className="flex-1 py-2 rounded-lg font-mono text-[11px] font-bold"
                              style={{
                                background: '#fff',
                                color: '#0f1611',
                                border: '1px solid rgba(15,22,17,0.15)',
                              }}
                            >
                              {p
                                ? p.p1Name.split(' ')[0] + '/' + p.p2Name.split(' ')[0]
                                : `Lag ${team}`}
                              <span
                                className="block text-[9px] font-normal"
                                style={{ color: '#6b766f' }}
                              >
                                møtte ikke → motstander vinner WO
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setWoOpen(false)}
                        className="w-full mt-1.5 font-mono text-[10px] uppercase"
                        style={{ color: '#6b766f' }}
                      >
                        Avbryt
                      </button>
                    </div>
                  )}
                  <div
                    className="flex items-center justify-between mt-3 pt-3 gap-2"
                    style={{ borderTop: '1px solid rgba(15,22,17,0.08)' }}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={undo}
                        disabled={history.length === 0}
                        className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded active:scale-95"
                        style={{
                          background: 'transparent',
                          color: history.length ? '#3a4a40' : '#c2c9c4',
                          border: '1px solid rgba(15,22,17,0.12)',
                          cursor: history.length ? 'pointer' : 'not-allowed',
                        }}
                      >
                        ↶ Angre
                      </button>
                      {confirmReset ? (
                        <button
                          onClick={() => {
                            setSets([{ t1: 0, t2: 0 }]);
                            setHistory([]);
                            setPadSide(null);
                            setConfirmReset(false);
                            persistLive([{ t1: 0, t2: 0 }]);
                          }}
                          className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded font-bold"
                          style={{ background: '#dc2626', color: '#fff' }}
                        >
                          Bekreft nullstill?
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setConfirmReset(true);
                            setTimeout(() => setConfirmReset(false), 3000);
                          }}
                          className="font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded"
                          style={{
                            background: 'transparent',
                            color: '#6b766f',
                            border: '1px solid rgba(15,22,17,0.10)',
                          }}
                        >
                          ↺ Nullstill
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() =>
                        canSend &&
                        onSaveScore &&
                        onSaveScore({ ...active, score: buildScore(), completedAt: Date.now() })
                      }
                      disabled={!canSend}
                      className="px-5 py-2 rounded-lg font-mono text-xs font-bold uppercase tracking-wider active:scale-95 flex items-center gap-1.5"
                      style={{
                        background: canSend ? '#0f1611' : '#eef0ea',
                        color: canSend ? '#c2f02f' : '#94a098',
                        cursor: canSend ? 'pointer' : 'not-allowed',
                      }}
                    >
                      ✓ Send resultat
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="px-4 py-4 text-center text-xs italic" style={{ color: muted }}>
            {completed.length > 0 && upcoming.length === 0
              ? 'Banen er ferdig brukt'
              : 'Ingen aktiv kamp akkurat nå'}
          </div>
        )}

        {/* Upcoming matches */}
        {upcoming.length > 0 && (
          <div className="px-4 py-2.5 space-y-2" style={{ background: '#ffffff04' }}>
            <div
              className="text-[10px] font-mono uppercase tracking-wider"
              style={{ color: muted }}
            >
              Kommer opp
            </div>
            {upcoming.map((m) => {
              const p1 = getPair(m.team1?.pairId),
                p2 = getPair(m.team2?.pairId);
              const di = matchDivInfo(m);
              // Full names: the pad is wide and read from a distance, so «Fredrik
              // Hansen / Marius Olsen» beats first names or surnames alone.
              const pairLabel = (p: any) => `${p.p1Name} / ${p.p2Name}`;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-2 text-[12px]"
                  style={{ color: muted }}
                >
                  <span className="font-mono text-[10px]" style={{ color: '#5a6a5f' }}>
                    {m.startTime}
                  </span>
                  {di && <KMClassTag gender={di.gender} divName={di.divName} />}
                  <span className="truncate flex-1">{p1 ? pairLabel(p1) : 'venter'}</span>
                  <span style={{ color: '#5a6a5f' }}>vs</span>
                  <span className="truncate flex-1 text-right">
                    {p2 ? pairLabel(p2) : 'venter'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {/* Spacer — always provides scroll room so the active match can stay at a fixed position */}
        <div style={{ height: '55%' }} />
      </div>
      {footer}
    </div>
  );
}
